<template>
  <div
    class="notification-item"
    role="button"
    tabindex="0"
    @click="$emit('open')"
    @keydown.enter="onActivate"
    @keydown.space.prevent="onActivate"
  >
    <div class="notification-item__main">
      <div class="notification-item__content">
        <BaseTag v-if="isSubjectLoading || tagLabel" :skeleton="isSubjectLoading" tone="neutral" preset="soft">
          <div class="notification-item__tag">
            <PlatformIcon v-if="sourceId" :platform="sourceId" />
            <span class="notification-item__tag-text">{{ tagLabel }}</span>
          </div>
        </BaseTag>
        <BaseLoadingValue class="notification-item__title-loader" :state-loading="isSubjectLoading" type="text">
          <template #content>
            <p class="notification-item__title" :class="{ 'is-unread': isUnread }">{{ title }}</p>
          </template>
        </BaseLoadingValue>

        <div class="notification-item__meta">
          <p class="notification-item__subtitle" :class="{ 'is-unread': isUnread }">{{ subtitle }}</p>
          <p class="notification-item__timestamp">{{ timeAgo }}</p>
        </div>
      </div>

      <button
        v-if="isUnread"
        class="notification-item__read-indicator"
        :aria-label="$t('notifications-center.mark-read')"
        v-tooltip="{ content: $t('notifications-center.mark-read'), placement: 'top' }"
        @click.stop="$emit('mark-read')"
      >
        <span class="notification-item__dot"></span>
        <BaseIcon name="check" class="notification-item__check" />
      </button>
      <div v-else class="notification-item__read-spacer"></div>
    </div>

    <div v-if="renderableTenderChanges.length > 0" class="notification-item__updates">
      <NotificationUpdateCard
        v-for="(tenderChange, index) in renderableTenderChanges"
        :key="index"
        :tender-change="tenderChange"
        :unread="isUnread"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingValue from '@/components/base/BaseLoadingValue.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import NotificationUpdateCard from '@/components/notifications-center/NotificationUpdateCard.vue';
import { NotificationSlugs, NotificationSubjectTypes } from '@/util/constants/notificationSlugs';
import { filterRenderableTenderChanges } from '@/util/notification-changes-util';
import { getFullName } from '@/util/user-util';
import PlatformIcon from '@/components/PlatformIcon.vue';

export default {
  name: 'NotificationItem',
  components: {
    PlatformIcon,
    BaseIcon,
    BaseLoadingValue,
    BaseTag,
    NotificationUpdateCard,
  },
  props: {
    notification: {
      type: Object,
      required: true,
    },
    tender: {
      type: Object,
      default: undefined,
    },
    opportunity: {
      type: Object,
      default: undefined,
    },
  },
  computed: {
    ...mapGetters('notifications', ['tendersPending', 'opportunitiesPending']),
    ...mapGetters('tenant', ['users']),
    isUnread() {
      return !this.notification.isRead;
    },
    isOpportunityEvent() {
      return this.notification.content.subjectType === NotificationSubjectTypes.OPPORTUNITY;
    },
    isVoteInvitation() {
      return this.notification.content.eventType === NotificationSlugs.OPPORTUNITY_VOTE_INVITATION;
    },
    isOpportunitySummary() {
      return this.notification.content.eventType === NotificationSlugs.OPPORTUNITY_SUMMARY;
    },
    payload() {
      return this.notification.content.payload || {};
    },
    // The summary event snapshots the whole opportunity in its payload; other events carry at most its name.
    payloadOpportunity() {
      return this.payload.opportunity || {};
    },
    isSubjectLoading() {
      if (this.isOpportunityEvent) return !this.opportunity && this.opportunitiesPending;
      return !this.tender && this.tendersPending;
    },
    title() {
      if (this.isSubjectLoading) return '';
      if (this.isOpportunityEvent) {
        if (this.opportunity) return this.opportunity.name;
        // Some events snapshot the name in their payload, so the row stays readable if the fetch failed.
        return (
          this.payload.opportunityName ||
          this.payloadOpportunity.name ||
          this.$t('notifications-center.unknown-opportunity')
        );
      }
      return this.tender ? this.tender.title : this.$t('notifications-center.unknown-tender');
    },
    subjectTender() {
      if (this.isOpportunityEvent) {
        return this.opportunity ? this.opportunity.tender : this.payloadOpportunity.tender || null;
      }
      return this.tender;
    },
    referenceNumber() {
      return this.subjectTender && this.subjectTender.source ? this.subjectTender.source.referenceNumber : null;
    },
    sourceId() {
      return this.subjectTender && this.subjectTender.source ? this.subjectTender.source.id : null;
    },
    tagLabel() {
      if (this.referenceNumber) return this.$t('tender-details.reference-num', { number: this.referenceNumber });
      return null;
    },
    inviterName() {
      // The roster is undefined until tenant/getTenant resolves; the computed re-evaluates once it lands.
      const inviter = (this.users || []).find((user) => user.id === this.payload.userInviterId);
      return inviter ? getFullName(inviter) : this.$t('unknown.user');
    },
    senderName() {
      // The opportunity summary payload embeds the sender, so no roster lookup is needed.
      return this.payload.sender ? getFullName(this.payload.sender) : this.$t('unknown.user');
    },
    subtitleKey() {
      const { eventType } = this.notification.content;
      if (eventType === NotificationSlugs.OPPORTUNITY_VOTE_INVITATION) {
        return 'notifications-center.subtitle.vote-invitation';
      }
      if (eventType === NotificationSlugs.OPPORTUNITY_SUMMARY) {
        return 'notifications-center.subtitle.summary-sent';
      }
      return [NotificationSlugs.OPPORTUNITY_ASSIGNATIONS, NotificationSlugs.TENDER_ASSIGNATIONS].includes(eventType)
        ? 'notifications-center.subtitle.assigned'
        : 'notifications-center.subtitle.results-updated';
    },
    subtitleParams() {
      if (this.isVoteInvitation) return { userName: this.inviterName };
      if (this.isOpportunitySummary) return { userName: this.senderName };
      return {};
    },
    subtitle() {
      return this.$t(this.subtitleKey, this.subtitleParams);
    },
    timeAgo() {
      return moment(this.notification.createdAt).fromNow();
    },
    renderableTenderChanges() {
      return filterRenderableTenderChanges(this.payload.tenderChanges);
    },
  },
  methods: {
    onActivate(event) {
      // The mark-as-read button lives inside the row and handles its own keys.
      if (event.target !== event.currentTarget) return;

      this.$emit('open');
    },
  },
};
</script>

<style lang="scss" scoped>
.notification-item {
  display: flex;
  flex-direction: column;
  gap: $space-sm;
  width: 100%;
  padding: $space-sm;
  border-radius: $radius-md;
  cursor: pointer;

  &:hover {
    background: $color-hover;
  }

  &:focus-visible {
    outline: 2px solid $color-accent-500;
    outline-offset: -2px;
  }

  &__main {
    display: flex;
    align-items: center;
    gap: $space-sm;
    width: 100%;
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: $space-1;
    flex: 1;
    min-width: 0;
  }

  // A long reference (an RFQ's category in it) ends in "…" within the item: a tag otherwise grows
  // to its text, past a phone's edge.
  .base-tag {
    max-width: 100%;

    ::v-deep .base-tag__label {
      overflow: hidden;
    }
  }

  &__tag {
    display: flex;
    gap: 2px;
    min-width: 0;

    > :not(.notification-item__tag-text) {
      flex-shrink: 0;
    }
  }

  &__tag-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__title-loader {
    max-width: 100%;
  }

  &__title {
    max-width: 100%;
    margin: 0;
    overflow: hidden;
    color: $color-text-title;
    font-size: $font-size-root;
    text-overflow: ellipsis;
    white-space: nowrap;

    // Phones list the notifications on a page of their own: room for a second line.
    @include screen-below('tablet') {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      white-space: normal;
    }
  }

  &__meta {
    display: flex;
    align-items: baseline;
    gap: $space-1;
    max-width: 100%;
    min-width: 0;
  }

  &__subtitle {
    margin: 0;
    overflow: hidden;
    color: $color-text-body;
    font-size: $font-size-xsmall;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__title.is-unread,
  &__subtitle.is-unread {
    font-weight: $font-weight-bold;
  }

  &__timestamp {
    flex-shrink: 0;
    margin: 0;
    color: $color-text-muted;
    font-size: $font-size-xsmall;
  }

  &__read-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: $radius-full;
    background: none;
    cursor: pointer;

    // A finger needs more than the dot; the negative margin keeps the row as it is.
    @include no-hover {
      width: 44px;
      height: 44px;
      margin: -8px;
    }

    &:hover {
      background: $color-accent-100;

      .notification-item__dot {
        display: none;
      }

      .notification-item__check {
        display: block;
      }
    }
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: $radius-full;
    background: $color-accent-500;
  }

  &__check {
    display: none;
    width: 16px;
    height: 16px;
    color: $color-accent-500;
  }

  &__read-spacer {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
  }

  &__updates {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }
}
</style>
